import { FC, ReactElement, memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { DnsRecordsQueryKeys, useDeleteDnsRecordsMutation, useGetOneByIdDnsRecordsQuery } from '@/modules/DnsRecords';

import { DnsRecordsOverviewView } from './DnsRecordsOverviewView';

interface Props {
  dnsRecordId: string;
}

export const DnsRecordsOverviewContainer: FC<Props> = memo(({ dnsRecordId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: dnsRecordData,
    error: dnsRecordError,
    isLoading: isDnsRecordsLoading,
    isFetching: isDeleteDnsRecordFetching,
    isError: isDnsRecordsError
  } = useGetOneByIdDnsRecordsQuery(dnsRecordId)({ enabled: !!dnsRecordId });

  const { isLoading: isDeleteDnsRecordLoading, mutate: deleteDnsRecordMutation } = useDeleteDnsRecordsMutation(
    dnsRecordId
  )({
    onSuccess: async () => {
      appMessageApi.success('DNS запис успішно видалено!');

      navigate(AppRoutes.DnsRecords);

      await queryClient.refetchQueries([DnsRecordsQueryKeys.GetAll]);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const handleDeleteDnsRecord = useCallback((): void => {
    deleteDnsRecordMutation();
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isDnsRecordsLoading }
      isError={ isDnsRecordsError }
      error={ dnsRecordError }
      data={ dnsRecordData }
    >
      {
        (dnsRecord): ReactElement => (
          <DnsRecordsOverviewView
            dnsRecord={ dnsRecord }
            isDeleteDnsRecordLoading={ isDeleteDnsRecordLoading }
            isDeleteDnsRecordDisabled={ isDeleteDnsRecordFetching || isDnsRecordsLoading }
            handleDeleteDnsRecord={ handleDeleteDnsRecord }
          />
        )
      }
    </FallbackedLayout>
  );
});
